/**
 * dijitalRozet.js — Dijital Rozet entegrasyonu için React Query hook'ları.
 *
 * Backend: backend/routes/dijital_rozet_admin.py
 * Uçlar (hepsi /admin/dijital-rozet/ altında):
 *   status · skills · courses · courses/{id} · courses/{id}/sync-detail
 *   certificates · certificates/{id}/retry · sweep · silme-kuyrugu/ozet
 *
 * Not: Beceri araması kullanıcı yazdıkça değil, açıkça arandığında tetiklenir
 * (kütüphane araması düz metin eşleşmesi yapıyor, gürültülü sonuç üretiyor —
 * her tuşta istek atmak hem gereksiz hem yanıltıcı).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

// ─── Query key factory ────────────────────────────────────────────────────────

export const dijitalRozetKeys = {
  all: ['dijitalRozet'],
  status: (ping) => [...dijitalRozetKeys.all, 'status', ping],
  skills: (q, lang) => [...dijitalRozetKeys.all, 'skills', q, lang],
  courses: (onlyConfigured) => [...dijitalRozetKeys.all, 'courses', onlyConfigured],
  certificates: (filters) => [...dijitalRozetKeys.all, 'certificates', filters],
  silmeKuyruguOzeti: ['dijitalRozet', 'silme-kuyrugu-ozeti'],
};

// ─── Durum ────────────────────────────────────────────────────────────────────

/** Entegrasyon ayarları. ping=true ise canlı bağlantı da denenir. */
export function useDijitalRozetStatus(ping = false) {
  return useQuery({
    queryKey: dijitalRozetKeys.status(ping),
    queryFn: async () => {
      const res = await apiClient.get(`/admin/dijital-rozet/status?ping=${ping ? 'true' : 'false'}`);
      return res.data;
    },
    staleTime: 60_000,
  });
}

// ─── Beceri arama ─────────────────────────────────────────────────────────────

/**
 * Beceri kütüphanesi araması. `enabled` false iken istek atılmaz —
 * çağıran taraf "Ara" butonuna basılınca açar.
 */
export function useSkillSearch(query, lang = 'tr', enabled = false) {
  return useQuery({
    queryKey: dijitalRozetKeys.skills(query, lang),
    queryFn: async () => {
      const params = new URLSearchParams({ q: query, lang, length: '20' });
      const res = await apiClient.get(`/admin/dijital-rozet/skills?${params}`);
      return res.data;
    },
    enabled: enabled && Boolean(query?.trim()),
    staleTime: 5 * 60_000,
  });
}

// ─── Kurslar ──────────────────────────────────────────────────────────────────

export function useDijitalRozetCourses(onlyConfigured = false) {
  return useQuery({
    queryKey: dijitalRozetKeys.courses(onlyConfigured),
    queryFn: async () => {
      const res = await apiClient.get(
        `/admin/dijital-rozet/courses?only_configured=${onlyConfigured ? 'true' : 'false'}`,
      );
      return res.data;
    },
  });
}

/** Kursun rozet ayarlarını kaydeder — dış API'ye çağrı yapmaz. */
export function useUpdateCourseRozetSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ courseId, data }) =>
      apiClient.put(`/admin/dijital-rozet/courses/${courseId}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: dijitalRozetKeys.all }),
  });
}

/** Kursu Dijital Rozet Detail kaydına iter (oluşturur veya günceller). */
export function useSyncCourseDetail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (courseId) =>
      apiClient.post(`/admin/dijital-rozet/courses/${courseId}/sync-detail`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: dijitalRozetKeys.all }),
  });
}

// ─── Rozet kuyruğu ────────────────────────────────────────────────────────────

export function useDijitalRozetCertificates(status = '', page = 1, pageSize = 20) {
  return useQuery({
    queryKey: dijitalRozetKeys.certificates({ status, page, pageSize }),
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (status) params.set('status', status);
      const res = await apiClient.get(`/admin/dijital-rozet/certificates?${params}`);
      return res.data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useRetryCertificateRozet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (certId) =>
      apiClient.post(`/admin/dijital-rozet/certificates/${certId}/retry`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: dijitalRozetKeys.all }),
  });
}

// ─── KVKK silme kuyruğu ─────────────────────────────────────────────────────────

/**
 * KVKK silme kuyruğu özeti (bekleyen kayıt sayısı + en eski kaydın yaşı).
 *
 * Entegrasyon kapalıyken (DIJITAL_ROZET_ENABLED=false) worker koşmaz ve bu
 * kuyruk sessizce birikir — sayaç bu riski görünür kılmak için periyodik
 * yenilenir, kullanıcı sayfayı açık bırakıp unutsa bile.
 */
export function useDijitalRozetSilmeKuyruguOzeti() {
  return useQuery({
    queryKey: dijitalRozetKeys.silmeKuyruguOzeti,
    queryFn: async () => {
      const res = await apiClient.get('/admin/dijital-rozet/silme-kuyrugu/ozet');
      return res.data;
    },
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

// ─── Sweeper ──────────────────────────────────────────────────────────────────

/**
 * Sweeper'ı tek geçiş çalıştırır (backend services/dijital_rozet_sweeper.py).
 *
 * `apply` verilmezse KURU KOŞUDUR: hiçbir dış çağrı ve hiçbir kayıt değişikliği
 * olmaz. Sandbox olmadığı (canlı bakanlık sistemine yazıldığı) için yayınlama
 * açık onay ister — arayüz önce kuru koşuyu gösterir, sonra uygulatır.
 */
export function useRunSweep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ apply = false, limit = 25, courseId, certificateId } = {}) => {
      const params = new URLSearchParams({
        apply: apply ? 'true' : 'false',
        limit: String(limit),
      });
      if (courseId) params.set('course_id', courseId);
      if (certificateId) params.set('certificate_id', certificateId);
      return apiClient.post(`/admin/dijital-rozet/sweep?${params}`).then((r) => r.data);
    },
    onSuccess: (_data, vars) => {
      if (vars?.apply) qc.invalidateQueries({ queryKey: dijitalRozetKeys.all });
    },
  });
}
